import { Scene } from '../Scene';
import { CrossBillboardMesh, GltfMesh } from '../../mesh';
import { Material } from '../../material';
import { ImpostorTextureGenerator } from '../../renderer/ImpostorTextureGenerator';
import { InstanceGroup } from '../../renderer/InstanceGroup';
import { RenderObject } from '../../object/RenderObject';
import { ParsedModel } from './ModelLoader';

const DEFAULT_FADE_BAND = 0.1;

interface DecimatedTier {
  mesh: GltfMesh;
  maxDistance: number;
  /**
   * Source-primitive materialIndex on the decimated GLB mesh. Used by the
   * far-objects loader (when farInclude lifts the Skip cull and the model has
   * no billboard) to resolve a Material directly without going through a near
   * InstanceGroup. Near-group LOD registration ignores this field — decimated
   * tiers render with the host group's existing material.
   */
  materialIndex?: number;
}

interface BillboardConfig {
  distance: number;
}

interface SkipConfig {
  distance: number;
}

/**
 * Per-model LOD setup that can be applied to any [InstanceGroup] for that model.
 *
 * The pre-built decimated meshes and (lazily-built) billboard mesh + impostor
 * texture are shared across every group of the same model — so a tile-streamed
 * group that gets created mid-game picks up the same LOD config as a
 * scene-loaded one without re-doing GPU resource allocation.
 */
export interface ModelLodKit {
  modelName: string;
  decimatedTiers: DecimatedTier[];
  /** Highest-tier decimated mesh, used for shadow casting. */
  shadowMesh: GltfMesh | null;
  fadeBand: number;
  /** Billboard distance threshold; null when the model has no billboard tier. */
  billboardConfig: BillboardConfig | null;
  /** Lazy-built on first use (requires a sample [RenderObject] to render). */
  billboardMesh: CrossBillboardMesh | null;
  billboardMaterial: Material | null;
  /** Skip distance threshold; null when the model has no skip tier. */
  skipConfig: SkipConfig | null;
}

interface ModelLodConfig {
  entries: (
    | { kind: 'decimated'; distance: number; ratio: number }
    | { kind: 'billboard'; distance: number }
    | { kind: 'skip'; distance: number }
  )[];
  crossfadeBand?: number;
}

/**
 * Build a [ModelLodKit] from a parsed model's GLB extras. Decimated tiers are
 * materialised eagerly (one [GltfMesh] per `_LOD_TIER` entry). Billboard tier
 * setup is deferred to the first group it's applied to — generating the
 * impostor texture requires rendering a sample [RenderObject].
 *
 * Returns null when the model has no `_MODEL_LOD` extras or no usable tiers.
 */
export function buildModelLodKit(scene: Scene, modelName: string, model: ParsedModel): ModelLodKit | null {
  const lodConfig = (model.json.extras?.['_MODEL_LOD'] as ModelLodConfig | undefined) ?? null;
  if (!lodConfig) return null;

  const fadeBand = lodConfig.crossfadeBand ?? DEFAULT_FADE_BAND;

  // Decimated tiers: meshes carrying mesh-level _LOD_TIER extras, sorted by tier.
  const gltfMeshes = model.json.meshes ?? [];
  type LodMeshEntry = { tier: number; sourceMesh: number; meshIdx: number; maxDistance: number };
  const lodEntries: LodMeshEntry[] = [];
  for (let meshIdx = 0; meshIdx < gltfMeshes.length; meshIdx++) {
    const mesh = gltfMeshes[meshIdx];
    const tier = mesh.extras?.['_LOD_TIER'] as number | undefined;
    if (tier === undefined) continue;
    const sourceMesh = mesh.extras?.['_LOD_SOURCE_MESH'] as number | undefined;
    const firstPrim = mesh.primitives[0];
    const maxDistance = firstPrim?.extras?.['_LOD']?.maxDistance as number | undefined;
    if (sourceMesh === undefined || maxDistance === undefined) continue;
    lodEntries.push({ tier, sourceMesh, meshIdx, maxDistance });
  }
  lodEntries.sort((a, b) => a.tier - b.tier);

  const decimatedTiers: DecimatedTier[] = [];
  let shadowMesh: GltfMesh | null = null;
  for (const entry of lodEntries) {
    const resolved = model.meshes[entry.meshIdx];
    const lodPrim = resolved?.primitives[0];
    if (!lodPrim) continue;
    const lodMesh = new GltfMesh(
      `${modelName}_lod_t${entry.tier}_m${entry.sourceMesh}`,
      scene.ctx,
      scene,
      lodPrim,
    );
    decimatedTiers.push({
      mesh: lodMesh,
      maxDistance: entry.maxDistance,
      materialIndex: lodPrim.materialIndex,
    });
    shadowMesh = lodMesh; // last assignment wins → highest tier
  }

  const billboardEntry = lodConfig.entries.find((e) => e.kind === 'billboard');
  const billboardConfig = billboardEntry ? { distance: billboardEntry.distance } : null;

  const skipEntry = lodConfig.entries.find((e) => e.kind === 'skip');
  const skipConfig = skipEntry ? { distance: skipEntry.distance } : null;

  if (decimatedTiers.length === 0 && !billboardConfig && !skipConfig) return null;

  return {
    modelName,
    decimatedTiers,
    shadowMesh,
    fadeBand,
    billboardConfig,
    billboardMesh: null,
    billboardMaterial: null,
    skipConfig,
  };
}

/**
 * Build the kit's billboard mesh + impostor material from a sample object if
 * not yet built. Idempotent — once cached on the kit, subsequent calls return
 * `true` immediately. Returns `false` when the kit has no billboard config or
 * when the sample's bounds are missing.
 *
 * Used by both [applyModelLodKitToGroup] (with a fresh group's first member)
 * and the far-objects loader (with a temporary full-mesh RenderObject).
 */
export function ensureKitBillboard(scene: Scene, kit: ModelLodKit, sampleObj: RenderObject): boolean {
  if (!kit.billboardConfig) return false;
  if (kit.billboardMesh && kit.billboardMaterial) return true;
  if (!sampleObj?.bounds) return false;
  const localMin = sampleObj.bounds.localMin;
  const localMax = sampleObj.bounds.localMax;
  const width = Math.max(localMax[0] - localMin[0], localMax[2] - localMin[2]);
  const height = localMax[1] - localMin[1];
  const offsetY = localMin[1];
  kit.billboardMesh = CrossBillboardMesh.create(
    `${kit.modelName}_billboard`,
    scene.ctx,
    scene,
    width,
    height,
    offsetY,
  );
  const impostorTexture = ImpostorTextureGenerator.generate(scene.ctx, sampleObj, 256);
  // Route through the gbuffer pass (with the shader's alpha-cutoff discard at
  // 0.5) instead of forward transparency. Forward transparency cannot instance
  // translucent materials, which would force one draw call per far billboard
  // — at scene scale that explodes the transparency pass. Edges are harder
  // than forward-transparent's mip-aware cutoff, but invisible at billboard
  // distances. The kit billboard's existing LOD-level use is unaffected: the
  // hosting InstanceGroup is already gbuffer-routed via its opaque base mesh.
  impostorTexture.translucent = false;
  const mat = new Material();
  mat.baseColor = impostorTexture;
  kit.billboardMaterial = mat;
  return true;
}

/**
 * Apply a [ModelLodKit] to a fresh [InstanceGroup]. Idempotency is the caller's
 * responsibility — only call this on a group whose `lodLevelCount === 0`. The
 * billboard mesh + impostor texture are built lazily here and cached on the kit
 * so subsequent groups reuse the same GPU resources.
 *
 * `suppressSkip` (used by far-include landmarks) skips installing the kit's
 * Skip-distance cull on this group, so the group's last registered LOD renders
 * to infinity instead of clipping. Note that `skipDistanceSq` is per-group, so
 * promoting one group of a model leaves any other group of the same model
 * unaffected — but every instance routed to the same `(mesh, material)` group
 * shares the lift.
 */
export function applyModelLodKitToGroup(
  scene: Scene,
  kit: ModelLodKit,
  group: InstanceGroup,
  suppressSkip: boolean = false,
): void {
  for (const tier of kit.decimatedTiers) {
    group.registerLod(tier.mesh, tier.maxDistance, false, undefined, kit.fadeBand);
  }
  if (kit.shadowMesh) {
    group.setShadowMesh(kit.shadowMesh);
  }

  if (kit.skipConfig && !suppressSkip) {
    group.skipDistanceSq = kit.skipConfig.distance * kit.skipConfig.distance;
  }

  if (!kit.billboardConfig) return;
  const sampleObj = group.members[0];
  if (!sampleObj || !ensureKitBillboard(scene, kit, sampleObj)) return;

  group.registerLod(
    kit.billboardMesh!,
    kit.billboardConfig.distance,
    true,
    kit.billboardMaterial!,
    kit.fadeBand,
    true, // isBillboard — excluded from depth/shadow passes
  );
}
